import { useEffect, useId, useRef } from 'react';

import { Callout } from '@/_shared/components/ui/callout';
import { Checkbox } from '@/_shared/components/ui/checkbox';
import { Input } from '@/_shared/components/ui/input';
import { cn } from '@/_shared/lib/utils';
import type { Question } from '@/_shared/types';

import { OptionDescription } from './OptionDescription';
import { OptionStat } from './OptionStat';
import { otherBlockClass } from './other-block';
import { multiTotal } from './selection';

interface Props {
  question: Question;
  checked: Set<string>;
  onCheckedChange: (next: Set<string>) => void;
  otherChecked: boolean;
  onOtherCheckedChange: (v: boolean) => void;
  otherText: string;
  onOtherTextChange: (v: string) => void;
  disabled?: boolean;
}

/** Layout shared by every option row. The fill and the ring are kept out of it: the Other row leaves both
 *  to the block that wraps it and its text box. */
const ROW_LAYOUT =
  'flex cursor-pointer items-start gap-3 rounded-md px-2.5 py-2 text-sm text-foreground transition-colors';

/** The controlled multi-select option list (checkbox rows + always-on Other + amber limit strip). */
export function MultiSelectList({
  question,
  checked,
  onCheckedChange,
  otherChecked,
  onOtherCheckedChange,
  otherText,
  onOtherTextChange,
  disabled,
}: Props): React.ReactElement {
  const baseId = useId();
  const otherInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (otherChecked) otherInputRef.current?.focus();
  }, [otherChecked]);

  const max = question.max;
  const atMax = max != null && multiTotal(checked, otherChecked) >= max;
  // At max, checking "Other" would push past the limit (Submit silently disables) — gate it like the
  // other unselected rows.
  const otherRowDisabled = Boolean(disabled) || (atMax && !otherChecked);

  // Show the at-max hint only when a still-selectable row is actually blocked — else it's just noise.
  const hasSelectableUnchosen = question.options.some((o) => !checked.has(o.label)) || !otherChecked;
  const showAtMaxHint = atMax && hasSelectableUnchosen;
  // Single source for the message so the visible strip and the SR announcement can't drift.
  const atMaxHint = `Maximum of ${max} reached — deselect one to change.`;

  const toggle = (label: string, on: boolean): void => {
    const next = new Set(checked);
    if (on) next.add(label);
    else next.delete(label);
    onCheckedChange(next);
  };

  // Unchecking abandons any free text — drop it so the input never shows an answer that
  // `buildMultiEntry` silently omits.
  const setOtherChecked = (on: boolean): void => {
    onOtherCheckedChange(on);
    if (!on) onOtherTextChange('');
  };

  const rowClass = (isChecked: boolean, rowDisabled: boolean): string =>
    cn(
      ROW_LAYOUT,
      // The focusable is the child checkbox, so mirror the radio row's focus ring via `has-` (the
      // utility keys on the element's own :focus-visible, which the row never gets).
      'has-focus-visible:ring-1 has-focus-visible:ring-ring has-focus-visible:ring-offset-2 has-focus-visible:ring-offset-background',
      // Title/description keep their full (foreground) color always — only the row background reacts to hover.
      isChecked ? 'bg-accent' : 'hover:bg-muted',
      rowDisabled && 'cursor-not-allowed opacity-50 hover:bg-transparent',
    );

  return (
    <>
      <div role="group" aria-label={question.prompt} className="grid gap-0.75">
        {question.options.map((opt, i) => {
          const isChecked = checked.has(opt.label);
          const rowDisabled = Boolean(disabled) || (atMax && !isChecked);
          const id = `${baseId}-${i}`;
          return (
            <label key={opt.label} htmlFor={id} className={rowClass(isChecked, rowDisabled)}>
              {/* Suppress the checkbox's own focus ring (ring-0) — the whole row shows the ring instead
                  (single ring, like the radio rows). */}
              <Checkbox
                id={id}
                checked={isChecked}
                disabled={rowDisabled}
                className="my-0.5 focus-visible:ring-0 focus-visible:ring-offset-0"
                onCheckedChange={(v) => toggle(opt.label, v === true)}
              />
              <span className="min-w-0 flex-1">
                <span className="block font-medium">{opt.label}</span>
                {opt.description && <OptionDescription content={opt.description} />}
              </span>
              {opt.stat && <OptionStat stat={opt.stat} />}
            </label>
          );
        })}

        {/* The row and its always-mounted box are one block, and `otherBlockClass` owns every surface the
            two share — so the row carries layout only, and the box sits inside the same wrapper. */}
        <div className={otherBlockClass(otherChecked, otherRowDisabled)}>
          <label
            htmlFor={`${baseId}-other`}
            // No `opacity-50` here: the block above owns the disabled fade so it covers the fill too.
            className={cn(ROW_LAYOUT, otherRowDisabled && 'cursor-not-allowed')}
          >
            {/* `data-other-row` marks the block's own control: the block rings on ITS focus, never on the
                box's, which draws a ring of its own. */}
            <Checkbox
              id={`${baseId}-other`}
              data-other-row
              checked={otherChecked}
              disabled={otherRowDisabled}
              className="my-0.5 focus-visible:ring-0 focus-visible:ring-offset-0 disabled:opacity-100"
              onCheckedChange={(v) => setOtherChecked(v === true)}
            />
            <span className="flex-1 font-medium">Other</span>
          </label>

          {/* Always mounted — typing auto-checks Other unless the row is at max and blocked. */}
          <div className="pr-2.5 pb-2 pl-9.5">
            <Input
              ref={otherInputRef}
              value={otherText}
              onChange={(e) => {
                const text = e.target.value;
                onOtherTextChange(text);
                // Real text claims the Other row and clearing it releases the row again, so a stray
                // keystroke can never leave Other checked-but-empty and Submit blocked.
                const hasText = text.trim().length > 0;
                if (hasText !== otherChecked) onOtherCheckedChange(hasText);
              }}
              placeholder="Type your own answer here"
              disabled={otherRowDisabled}
              aria-label="Your answer"
              maxLength={10000}
            />
          </div>
        </div>
      </div>

      {/* Always-mounted SR live region so a hint is announced when it appears (a region inserted
          together with its content is announced inconsistently). Mirrors QuestionWizard. */}
      <span className="sr-only" aria-live="polite">
        {showAtMaxHint ? atMaxHint : ''}
      </span>

      {showAtMaxHint && (
        // Visual-only — `aria-hidden` keeps it out of the a11y tree so the sr-only live region above is
        // the single announcement (otherwise SR users hit the same hint twice). Shown only when reaching
        // the max actually blocks a still-selectable row (an option or Other).
        <Callout variant="warning" aria-hidden className="my-1 text-xs">
          {atMaxHint}
        </Callout>
      )}
    </>
  );
}
